import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TITLES = {
  visit:         { title: "Customer Visit Assistant",       what: "Capture meeting notes, machine info, defect photos, follow-up reports, action items, and sample delivery schedules — all in one place." },
  cost:          { title: "Ink Cost Calculator",            what: "Coverage calculations, cost per thousand impressions (CPMi), ink consumption, BCM recommendations, anilox optimization, and ROI comparisons." },
  application:   { title: "Application Recommendation",     what: "Given a substrate (film, paper, foil, corrugated, folding carton, shrink sleeve), recommend the correct ink technology and additives." },
  samples:       { title: "Sample & Trial Manager",         what: "Track sample requests, lab batches, on-press trials, customer approvals, production validation, and trial history — with automatic reminders." },
  competitive:   { title: "Competitive Intelligence Hub",   what: "Competitor products, strengths and weaknesses, pricing notes, key differentiators, and one-page battle cards for the sales team." },
  color:         { title: "Color Management Suite",         what: "Pantone lookup, Delta E calculations, spectrophotometer integration, drawdown history, and color tolerance tracking per customer." },
  proposals:     { title: "Sales Proposal Generator",       what: "Technical proposals, trial reports, cost savings summaries, product recommendations, ROI calculations, and customer presentations — auto-generated." },
  process:       { title: "Manufacturing Process Analyzer", what: "Open-ended questions like 'Why is this mottling?' or 'What anilox should I recommend?' — structured, ranked AI recommendations." },
  accounts:      { title: "Account Intelligence Dashboard", what: "Installed equipment, current ink systems, annual usage, open opportunities, customer pain points, visit history, and revenue trends per account." },
};

export default function InkComingSoon() {
  const { slug } = useParams();
  const meta = TITLES[slug] || { title: "Coming soon", what: "" };
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Valid email required"); return; }
    setLoading(true);
    try {
      await api.post("/public/lead", {
        email,
        source: `ink_early_access_${slug}`,
        company: note,
      });
      setDone(true);
    } catch { toast.error("Could not submit"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <Link to="/app/ink" className="inline-flex items-center gap-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground mb-8">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Ink &amp; Print
      </Link>

      <div className="text-xs font-mono-plex uppercase tracking-widest text-accent">◉ SHIPPING NEXT</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-3" data-testid="coming-soon-title">
        {meta.title}
      </h1>
      <p className="text-muted-foreground mt-4 leading-relaxed max-w-2xl">
        {meta.what}
      </p>

      <div className="mt-10 border border-border bg-card rounded-sm p-8">
        {done ? (
          <div className="text-center py-6" data-testid="coming-soon-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <div className="font-display font-bold text-2xl mt-4">You&rsquo;re on the list.</div>
            <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto">
              We&rsquo;ll email you the moment this module ships — and you&rsquo;ll be among the first to shape what it looks like.
            </p>
          </div>
        ) : (
          <>
            <div className="overline text-primary">◆ REQUEST EARLY ACCESS</div>
            <h2 className="font-display font-bold text-2xl mt-2">Get in the room when we build this</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Founding members shape the roadmap. Drop your email + one line about how you&rsquo;d use it.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="early-access-form">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="rounded-sm h-11"
                data-testid="coming-soon-email"
                required
              />
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="How would you use this?"
                rows={3}
                className="rounded-sm resize-none"
                data-testid="coming-soon-note"
                maxLength={500}
              />
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="coming-soon-submit">
                {loading ? "Sending…" : (<><Sparkles className="w-4 h-4 mr-2" /> Request early access</>)}
              </Button>
            </form>
          </>
        )}
      </div>

      <div className="mt-8 text-center">
        <Link to="/app/ink/copilot" className="text-sm font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-primary">
          Meanwhile → Try the AI Technical Sales Copilot
        </Link>
      </div>
    </div>
  );
}
